// rules-card.ts — (tf3-38; Creative 2 C2.0-2 §3) the rules card at the storyboard gate, pure: the backend's read, an edit
// made from the form (only the fields changed, in the card's types), whether the run is where a card can be edited (parked
// at its storyboard gate, or held by the storyboard check), and when Approve may show again after an edit.
// The workers decide; these never guess past what they read.

/** contracts RulesCard as the workers store it (the backend reads it with that schema before it reaches here) */
export interface RulesCardView {
  version: number; source: "references" | "fallback"; sourceNote: string; videoTypeId: string | null; videoRole: "pilot" | "episode" | "single";
  persona: string; personaDerived: boolean; ageBand: "young" | "adult" | "mature" | null; world: string | null;
  hookSec: number; pacingId: string | null; secondsPerCut: number | null; tone: string | null; artStyle: string | null;
  takeStyle: "cuts" | "aroll_with_cutaways" | "continuous_preferred"; takeStyleRendered: "cuts" | "aroll_with_cutaways";
  maxOpenLoops: number; rehookAllowed: boolean; maxCutawaySec: number | null; returnEveryNBeats: number | null;
}
/** GET /api/ops/runs/:runId/rules-card */
export interface RulesCardRead { ok: true; card: RulesCardView; gateRules: { id: string; version: string; rules: { id: string; text: string }[] }; editable: string[] }
/** POST /api/ops/runs/:runId/rules-card → 200 */
export interface RulesCardSaved { ok: true; card: RulesCardView; reset: string; stepsReset: number }   // (audit 10-23) the workers' count of steps reset

type Kind = "text" | "text?" | "num" | "num?" | "int" | "int?" | "bool" | "slug?" | { one: readonly string[]; empty?: true };
/** Each field a person may edit (contracts RULES_CARD_EDITABLE_FIELDS), its label and how its input is read. */
export const CARD_FIELDS: readonly { id: keyof RulesCardView; label: string; kind: Kind }[] = [
  { id: "persona", label: "Who it is for", kind: "text" },
  { id: "world", label: "Their world", kind: "text?" },
  { id: "ageBand", label: "Age band", kind: { one: ["young", "adult", "mature"], empty: true } },
  { id: "hookSec", label: "Hook within (seconds)", kind: "num" },
  { id: "pacingId", label: "Pacing (a library pacing id)", kind: "slug?" },
  { id: "secondsPerCut", label: "Seconds per cut (empty: the pacing's own)", kind: "num?" },
  { id: "tone", label: "Tone", kind: "text?" },
  { id: "artStyle", label: "Look", kind: "text?" },
  { id: "takeStyle", label: "Takes", kind: { one: ["cuts", "aroll_with_cutaways", "continuous_preferred"] } },
  { id: "maxOpenLoops", label: "Open loops at once (1–4)", kind: "int" },
  { id: "rehookAllowed", label: "Rehook tricks allowed", kind: "bool" },
  { id: "maxCutawaySec", label: "Longest cutaway (seconds; A-roll only)", kind: "num?" },
  { id: "returnEveryNBeats", label: "Back to the A-roll every N beats (A-roll only)", kind: "int?" },
];

/** The form's starting values: the card's own, as text ("" for none). */
export function formOf(card: RulesCardView): Record<string, string> {
  return Object.fromEntries(CARD_FIELDS.map((f) => { const v = card[f.id]; return [f.id, v === null || v === undefined ? "" : String(v)]; }));
}

/** Pure: the edit the form makes — only the fields that changed, in the card's types; or what's wrong, named. A field the
 *  workers don't offer (`editable`) is never sent. The workers check the result against the card's own rules (422). */
export function editOf(card: RulesCardView, form: Record<string, string>, editable: readonly string[]): { edit: Record<string, unknown> } | { error: string } {
  const edit: Record<string, unknown> = {};
  for (const f of CARD_FIELDS) {
    if (!editable.includes(f.id)) continue;
    const raw = (form[f.id] ?? "").trim();
    let v: unknown;
    const k = f.kind;
    if (typeof k === "object") {
      if (raw === "" && k.empty) v = null;
      else if (k.one.includes(raw)) v = raw;
      else return { error: `${f.label}: one of ${k.one.join(", ")}` };
    } else if (k === "bool") {
      if (raw !== "true" && raw !== "false") return { error: `${f.label}: yes or no` };
      v = raw === "true";
    } else if (k.endsWith("?") && raw === "") v = null;
    else if (k === "text" || k === "text?") { if (!raw) return { error: `${f.label}: can't be empty` }; v = raw; }
    else if (k === "slug?") { if (!/^[a-z][a-z0-9_]*$/.test(raw)) return { error: `${f.label}: lowercase letters, digits and _ (a library pacing id)` }; v = raw; }
    else {
      const n = Number(raw);
      if (raw === "" || !Number.isFinite(n) || ((k === "int" || k === "int?") && !Number.isInteger(n))) return { error: `${f.label}: ${k.startsWith("int") ? "a whole number" : "a number"}` };
      v = n;
    }
    if (v !== card[f.id]) edit[f.id] = v;
  }
  return Object.keys(edit).length ? { edit } : { error: "Nothing changed" };
}

/** Where a person may edit the card (the workers' own rule): parked at the storyboard gate, or held by the storyboard check. */
export function atStoryboard(awaitingGateStepId: string | null | undefined,
    steps: readonly { stepId: string; status: string; failureCategory?: string | null }[]): "gate" | "held" | null {
  if (awaitingGateStepId === "gate_storyboard") return "gate";
  return steps.some((s) => s.stepId === "storyboard_intent_check" && s.status === "failed" && s.failureCategory === "storyboard_intent_held") ? "held" : null;
}

/** Pure: after an edit, Approve stays hidden until a read STARTED after the edit shows the gate parked again (the workers
 *  send a parked gate back to waiting while what the edit re-runs runs). No edit: nothing hidden. */
/** (audit 10-21 F3) after an edit the Inspector reads the run again every REREAD_MS, for at most REREAD_LIMIT_MS */
export const REREAD_MS = 10_000, REREAD_LIMIT_MS = 10 * 60_000;
export const rereadDone = (editedAt: number, now: number) => now - editedAt >= REREAD_LIMIT_MS;
export const approveHidden = (editedAt: number | null, readStartedAt: number, parked: boolean) =>
  editedAt !== null && !(parked && readStartedAt > editedAt);

/** The edit's outcome in words: what re-runs, and that Approve comes back when the gate parks again. */
export function savedLine(s: RulesCardSaved): string {
  const what = s.reset === "continuity_direct" ? "the storyboard is re-made from continuity (a new pacing)" : "the storyboard check runs again on the new card";
  return `Saved as version ${s.card.version}: ${what}. Approve comes back when the gate parks again.`;
}
